import { useState } from "react";
import { Plus } from "lucide-react";

import {
  ApiKeysService,
  type ApiKeyDetail,
  type ApiKeyStatusFilter,
  type CreateApiKeyResponse,
} from "@/api";
import { useApi } from "@/hooks/use-api";
import { useTranslation } from "@/i18n";
import { parseApiError } from "@/lib/api-error";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/shared/page-header";

import { ApiAccessSection } from "@/components/api-keys/api-access-section";
import { ApiKeyCreateDialog } from "@/components/api-keys/api-key-create-dialog";
import { ApiKeyDisableDialog } from "@/components/api-keys/api-key-disable-dialog";
import { ApiKeyEditDialog } from "@/components/api-keys/api-key-edit-dialog";
import { ApiKeyRevokeDialog } from "@/components/api-keys/api-key-revoke-dialog";
import { ApiKeyShowOnceDialog } from "@/components/api-keys/api-key-show-once-dialog";
import { ApiKeysFilterBar } from "@/components/api-keys/api-keys-filter-bar";
import { ApiKeysList } from "@/components/api-keys/api-keys-list";
import { ApiKeysQuickstart } from "@/components/api-keys/api-keys-quickstart";

export default function ApiKeysPage() {
  const { t } = useTranslation();
  const [statusFilter, setStatusFilter] = useState<ApiKeyStatusFilter>("all");

  const [createOpen, setCreateOpen] = useState(false);
  const [showOnceKey, setShowOnceKey] = useState<CreateApiKeyResponse | null>(
    null
  );
  const [editTarget, setEditTarget] = useState<ApiKeyDetail | null>(null);
  const [disableTarget, setDisableTarget] = useState<ApiKeyDetail | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<ApiKeyDetail | null>(null);

  const {
    data: keys,
    error,
    isLoading,
    refetch,
  } = useApi(
    () =>
      ApiKeysService.list(
        statusFilter === "all" ? undefined : { status: statusFilter }
      ),
    { deps: [statusFilter] }
  );

  const hasNoKeysAtAll =
    !isLoading && statusFilter === "all" && (!keys || keys.length === 0);

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("apiKeys.page.title")}
        description={t("apiKeys.page.description")}
        action={
          <Button onClick={() => setCreateOpen(true)} size="sm">
            <Plus size={16} />
            {t("apiKeys.page.createButton")}
          </Button>
        }
      />

      {error && (
        <Alert variant="danger">{parseApiError(error)}</Alert>
      )}

      <ApiAccessSection />

      {hasNoKeysAtAll ? (
        <ApiKeysQuickstart onCreateClick={() => setCreateOpen(true)} />
      ) : (
        <>
          <ApiKeysFilterBar value={statusFilter} onChange={setStatusFilter} />
          <ApiKeysList
            keys={keys ?? []}
            isLoading={isLoading}
            onEdit={setEditTarget}
            onDisable={setDisableTarget}
            onRevoke={setRevokeTarget}
            onMutate={refetch}
          />
        </>
      )}

      <ApiKeyCreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSuccess={(created) => {
          setCreateOpen(false);
          setShowOnceKey(created);
          void refetch();
        }}
      />

      <ApiKeyShowOnceDialog
        keyData={showOnceKey}
        onClose={() => setShowOnceKey(null)}
      />

      <ApiKeyEditDialog
        apiKey={editTarget}
        onClose={() => setEditTarget(null)}
        onSuccess={() => {
          setEditTarget(null);
          void refetch();
        }}
      />

      <ApiKeyDisableDialog
        apiKey={disableTarget}
        onClose={() => setDisableTarget(null)}
        onSuccess={() => {
          setDisableTarget(null);
          void refetch();
        }}
      />

      <ApiKeyRevokeDialog
        apiKey={revokeTarget}
        onClose={() => setRevokeTarget(null)}
        onSuccess={() => {
          setRevokeTarget(null);
          void refetch();
        }}
      />
    </div>
  );
}
